import { axialToWorld, worldToAxial } from "@/src/core/engine/HexGrid";
import { snapPointToGrid } from "@/src/core/engine/Camera";
import { getWallObject } from "@/src/core/engine/wallObjects";
import { STAMP_CATALOG } from "@/src/core/engine/stampCatalog";
import { FLOOR_OBJECT_ASSET, getFloorObject } from "@/src/core/engine/floorObjects";
import { MARKER_OBJECT_ASSET, getMarkerObject } from "@/src/core/engine/markerObjects";
import type {
  FloorObjectType, MarkerObjectType, MultiPolygon, Point, StampAssetId, WallObjectType,
} from "@/src/types/map";

/**
 * Wall snapping used to live as a private helper inside MapCanvas.tsx, where it
 * ran once on the placing click and was then silently undone by the generic
 * half-grid drag that follows placement (D-022). It lives here now so both the
 * placing click and the drag share one implementation, and so the geometry is
 * reachable from tests without a DOM.
 */

/**
 * Grid intersections, or hex centres on a hex map. This is the lattice for
 * *drawing geometry* — room and polygon corners, label anchors, patch shapes —
 * which is deliberately not the same question as where a placed object's centre
 * belongs (D-024). A room corner wants a grid line; a crate wants a cell.
 */
export const geometryCentre = (
  unsnapped: Point,
  gridType: "square" | "hex",
  gridSizeWorld: number,
): Point => (gridType === "hex"
  ? axialToWorld(worldToAxial(unsnapped, gridSizeWorld), gridSizeWorld)
  : snapPointToGrid(unsnapped, gridSizeWorld));

/**
 * Which lattice one axis snaps to (D-024).
 *
 * Round the object's catalogue size in cells. An **even** count spans a whole
 * number of cells symmetrically about a grid **line**, so its centre belongs on
 * that line: a 2-cell table then covers exactly two cells. An **odd** count
 * belongs in the middle of a **cell**: a 1-cell crate fills its cell instead of
 * straddling four, and a 0.7-cell pillar sits in the middle of one.
 *
 * Sizes are rounded because the catalogue is full of 0.7s and 1.2s; the question
 * is only "how many cells does this roughly occupy", and the parity of that
 * answer is what decides the lattice. No catalogue entry currently sits on a .5
 * boundary, but Math.round is half-up, so 1.5 would count as 2 — pinned by test.
 */
export type AxisAlignment = "line" | "cell-centre";

export const axisAlignmentFor = (sizeCells: number): AxisAlignment =>
  (Math.round(sizeCells) % 2 === 0 ? "line" : "cell-centre");

export interface GridAlignment {
  x: AxisAlignment;
  y: AxisAlignment;
}

/** Grid intersections on both axes — what everything used before D-024. */
/**
 * The alignment for an object, derived from its catalogue size.
 *
 * Wall-snapped objects are deliberately excluded and keep grid intersections:
 * their position comes from the wall they sit on, and the lattice is only the
 * out-of-range fallback (D-022). Changing it would move doors.
 */
export const gridAlignmentFor = (
  assetId: StampAssetId | undefined,
  selectors: PlacementSelectors = {},
): GridAlignment => {
  if (placementModeFor(assetId, selectors) === "wall-snap") return { x: "line", y: "line" };
  const [widthCells, heightCells] = catalogueSizeCells(assetId, selectors);
  return { x: axisAlignmentFor(widthCells), y: axisAlignmentFor(heightCells) };
};

/** Snaps one coordinate to the nearest point of its axis lattice. */
export const snapAxis = (
  value: number,
  alignment: AxisAlignment,
  gridSizeWorld: number,
): number => (alignment === "line"
  ? Math.round(value / gridSizeWorld) * gridSizeWorld
  : Math.round((value - gridSizeWorld / 2) / gridSizeWorld) * gridSizeWorld + gridSizeWorld / 2);

/**
 * **The** lattice. Every placed object's centre comes from here — the placing
 * click and the drag that continues it both call this and nothing else.
 *
 * The lattice is derived from the object's own catalogue entry, so there is no
 * alignment parameter for a caller to get wrong. That is deliberate: two call
 * sites passing different lattices is exactly the fault D-022 removed, and it
 * had already grown back once inside its own fix before being caught.
 *
 * Hex maps are untouched — a hex has one centre and no notion of axis parity.
 */
export const placementCentreFor = (
  unsnapped: Point,
  assetId: StampAssetId | undefined,
  selectors: PlacementSelectors,
  gridType: "square" | "hex",
  gridSizeWorld: number,
): Point => {
  if (gridType === "hex") return geometryCentre(unsnapped, gridType, gridSizeWorld);
  const alignment = gridAlignmentFor(assetId, selectors);
  return [
    snapAxis(unsnapped[0], alignment.x, gridSizeWorld),
    snapAxis(unsnapped[1], alignment.y, gridSizeWorld),
  ];
};

/**
 * How far from a wall a pointer may be and still snap, in grid cells. Wide
 * enough to catch a comfortable click, narrow enough that the middle of a
 * 2-cell-wide room does not grab either wall.
 */
export const WALL_SNAP_RADIUS_CELLS = 0.8;

/** The asset id each typed family occupies. Families are keyed, not behavioural. */
export const WALL_OBJECT_ASSET = "door" as const;

/** How an object resolves its position. Declared per catalogue entry. */
export type PlacementMode = "wall-snap" | "grid-snap";

/** Anything with no typed family behind it places on the plain lattice. */
const DEFAULT_PLACEMENT: PlacementMode = "grid-snap";

/** The type fields that pick a catalogue entry out of its family. */
export interface PlacementSelectors {
  wallType?: WallObjectType;
  floorType?: FloorObjectType;
  markerType?: MarkerObjectType;
}

/**
 * One row per typed family: which asset id it occupies, and how to read the
 * placement mode off the catalogue entry the selectors point at. A table rather
 * than a chain of `assetId === ...` branches, so adding a family is a row.
 */
const FAMILIES: ReadonlyArray<{
  asset: StampAssetId;
  placementOf: (selectors: PlacementSelectors) => PlacementMode;
  sizeOf: (selectors: PlacementSelectors) => readonly [number, number];
}> = [
  { asset: WALL_OBJECT_ASSET,
    placementOf: (s) => getWallObject(s.wallType).placement,
    sizeOf: (s) => [getWallObject(s.wallType).defaultWidthCells, getWallObject(s.wallType).defaultHeightCells] },
  { asset: FLOOR_OBJECT_ASSET,
    placementOf: (s) => getFloorObject(s.floorType).placement,
    sizeOf: (s) => [getFloorObject(s.floorType).defaultWidthCells, getFloorObject(s.floorType).defaultHeightCells] },
  { asset: MARKER_OBJECT_ASSET,
    placementOf: (s) => getMarkerObject(s.markerType).placement,
    sizeOf: (s) => [getMarkerObject(s.markerType).defaultWidthCells, getMarkerObject(s.markerType).defaultHeightCells] },
];

/** The catalogue size in cells for an object, family entry or plain stamp. */
export const catalogueSizeCells = (
  assetId: StampAssetId | undefined,
  selectors: PlacementSelectors = {},
): readonly [number, number] => {
  const family = FAMILIES.find((entry) => entry.asset === assetId);
  if (family) return family.sizeOf(selectors);
  const stamp = assetId ? STAMP_CATALOG.find((entry) => entry.id === assetId) : undefined;
  return stamp ? [stamp.widthCells, stamp.heightCells] : [1, 1];
};

/**
 * The placement mode for an object, read from **the catalogue entry itself**.
 *
 * Snapping must never key off an asset id or a `wallType` literal. A window is
 * not special because it is called "window"; it is wall-snapped because its
 * catalogue row says `placement: "wall-snap"`. Adding an opening type is then a
 * catalogue row and a draw branch, with placement already correct — which is the
 * rule the wall family was built on (CLAUDE.md rule 14) and the one the old
 * `selectedStampAsset === "door"` check quietly broke.
 */
export const placementModeFor = (
  assetId: StampAssetId | undefined,
  selectors: PlacementSelectors = {},
): PlacementMode => {
  const family = FAMILIES.find((entry) => entry.asset === assetId);
  return family ? family.placementOf(selectors) : DEFAULT_PLACEMENT;
};

export interface WallSnapResult {
  /** The point on the wall segment closest to the probe. */
  point: Point;
  /** Degrees, matching the wall segment's direction. */
  rotation: number;
  /** Distance from the probe to `point`, in world units. */
  distance: number;
}

/**
 * Projects `point` onto the closest edge of `geometry` and returns that
 * projection with the edge's angle, or null when nothing is within
 * `maxDistance`. Callers centre the object on `point` and adopt `rotation`,
 * which is what makes an opening sit flush in the wall rather than beside it.
 */
export const nearestWallSegment = (
  point: Point,
  geometry: MultiPolygon,
  maxDistance: number,
): WallSnapResult | null => {
  let nearest: WallSnapResult | null = null;
  geometry.forEach((polygon) => polygon.forEach((ring) => {
    for (let index = 0; index < ring.length - 1; index += 1) {
      const start = ring[index];
      const end = ring[index + 1];
      if (!start || !end) continue;
      const deltaX = end[0] - start[0];
      const deltaY = end[1] - start[1];
      const lengthSquared = deltaX * deltaX + deltaY * deltaY;
      if (lengthSquared === 0) continue;
      const ratio = Math.max(0, Math.min(1,
        ((point[0] - start[0]) * deltaX + (point[1] - start[1]) * deltaY) / lengthSquared,
      ));
      const projected: Point = [start[0] + ratio * deltaX, start[1] + ratio * deltaY];
      const distance = Math.hypot(point[0] - projected[0], point[1] - projected[1]);
      if (distance <= maxDistance && (!nearest || distance < nearest.distance)) {
        nearest = { point: projected, rotation: (Math.atan2(deltaY, deltaX) * 180) / Math.PI, distance };
      }
    }
  }));
  return nearest;
};

export interface WallPlacement {
  /** Top-left corner, ready to assign to a MapObject. */
  x: number;
  y: number;
  rotation: number;
  /** False when nothing was in range and the caller's fallback point was used. */
  snapped: boolean;
}

/**
 * Turns a pointer position into a concrete placement for a wall object of the
 * given size. `fallback` is used unrotated when no wall is within range, so a
 * door can still be dropped in open space and dragged to a wall later.
 *
 * The size passed in must be the object's *actual* size. Deriving it from the
 * generic "door" stamp instead is what put windows and arrow slits inside rooms:
 * their real widths are 0.8 and 0.6 cells, so a half-cell offset taken from the
 * 1-cell stamp could never land their centre on a wall line.
 */
export const wallPlacementFor = (
  pointer: Point,
  assetId: StampAssetId | undefined,
  selectors: PlacementSelectors,
  width: number,
  height: number,
  geometry: MultiPolygon,
  gridType: "square" | "hex",
  gridSizeWorld: number,
): WallPlacement => {
  const wall = nearestWallSegment(pointer, geometry, gridSizeWorld * WALL_SNAP_RADIUS_CELLS);
  // The out-of-range fallback is computed here rather than accepted from the
  // caller. It used to be a parameter, and the two call sites passed different
  // lattices — the click the full grid, the drag the half-grid — so a wall
  // object dropped away from any wall still jumped when the pointer moved. One
  // gesture, one lattice; there is now no way to pass a second one.
  const centre = wall?.point ??
    placementCentreFor(pointer, assetId, selectors, gridType, gridSizeWorld);
  return {
    x: centre[0] - width / 2,
    y: centre[1] - height / 2,
    rotation: wall?.rotation ?? 0,
    snapped: wall !== null,
  };
};
